import React, { useState } from 'react';
import api from '../api/axios';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await api.post('/email/contact', formData);
      setSubmitStatus('success');
      setFormData({
        nom: '',
        email: '',
        telephone: '',
        sujet: '',
        message: ''
      });

      // Reset status après 5 secondes
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: '📍',
      title: 'Adresse',
      details: ['Hôpital Universitaire Justinien', 'Rue 17 Q, Cap-Haïtien', 'Haïti'],
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: '📞',
      title: 'Téléphone',
      details: ['22 60 12 92', 'Lundi - Vendredi', '8h00 - 16h00'],
      color: 'from-green-500 to-green-600'
    },
    {
      icon: '✉️',
      title: 'Email',
      details: ['contact@istmech.edu.ht', 'Réponse sous 24h', 'Support étudiant'],
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: '🌐',
      title: 'Site Web',
      details: ['www.istmech.edu.ht', 'Informations officielles', 'Actualités'],
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const sujets = [
    'Informations générales',
    'Admission et inscription',
    'Programme des études',
    'Frais de scolarité',
    'Stages et emploi',
    'Partenariats',
    'Autre'
  ];

  return (
    <section id="contact" className="py-16 bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Contactez-Nous
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Nous sommes là pour répondre à toutes vos questions sur l'ISTMECH.
            N'hésitez pas à nous contacter pour plus d'informations.
          </p>
        </div>

        {/* Informations de contact */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactInfo.map((info, index) => (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transform hover:scale-105 transition-all duration-300">
              <div className={`h-2 bg-gradient-to-r ${info.color}`}></div>
              <div className="p-6 text-center dark:text-white">
                <div className="text-4xl mb-4">{info.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{info.title}</h3>
                <div className="space-y-1">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className={`${idx === 0 ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'} text-sm`}>
                      {detail}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Formulaire de contact */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 transition-colors duration-200">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Envoyez-nous un message
            </h3>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-400 rounded-r-lg">
                <div className="flex items-center">
                  <span className="text-green-500 text-xl mr-3">✅</span>
                  <p className="text-green-800 dark:text-green-200 font-medium">
                    Votre message a été envoyé avec succès ! Nous vous répondrons bientôt.
                  </p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 dark:border-red-400 rounded-r-lg">
                <div className="flex items-center">
                  <span className="text-red-500 text-xl mr-3">⚠️</span>
                  <p className="text-red-800 dark:text-red-200 font-medium">
                    Une erreur est survenue lors de l'envoi. Veuillez réessayer ou utiliser l'email direct.
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="nom" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Votre nom complet"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="votre.email@exemple.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="+509 XX XX XX XX"
                  />
                </div>

                <div>
                  <label htmlFor="sujet" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sujet *
                  </label>
                  <select
                    id="sujet"
                    name="sujet"
                    value={formData.sujet}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  >
                    <option value="">Sélectionnez un sujet</option>
                    {sujets.map((sujet, index) => (
                      <option key={index} value={sujet}>{sujet}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 resize-vertical bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="Décrivez votre demande en détail..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 transform hover:scale-105'
                  } shadow-lg`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Envoi en cours...
                  </span>
                ) : (
                  'Envoyer le message'
                )}
              </button>
            </form>
          </div>

          {/* Carte et informations supplémentaires */}
          <div className="space-y-8">
            {/* Carte/Plan */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 transition-colors duration-200">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Notre Localisation
              </h3>

              {/* Placeholder pour carte */}
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 dark:from-gray-700 dark:to-gray-600 rounded-xl p-8 text-center mb-6">
                <div className="w-16 h-16 bg-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">ISTMECH</h4>
                <p className="text-blue-700 dark:text-blue-200">Hôpital Universitaire Justinien</p>
                <p className="text-blue-600 dark:text-blue-300">Rue 17 Q, Cap-Haïtien</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="text-blue-600 mr-3">🚗</span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Accès en voiture</p>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">Parking disponible sur site</p>
                  </div>
                </div>

                <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="text-green-600 mr-3">🚌</span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Transport public</p>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">Accessible par les transports en commun</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Horaires d'ouverture */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 transition-colors duration-200">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Horaires d'Ouverture
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="font-medium text-gray-900 dark:text-white">Lundi - Vendredi</span>
                  <span className="text-blue-600 font-semibold">8h00 - 16h00</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="font-medium text-gray-900 dark:text-white">Samedi</span>
                  <span className="text-gray-500 dark:text-gray-400">Fermé</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium text-gray-900 dark:text-white">Dimanche</span>
                  <span className="text-gray-500 dark:text-gray-400">Fermé</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  <strong>Note :</strong> Pour les urgences ou questions importantes,
                  vous pouvez nous contacter par email à tout moment.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section FAQ rapide */}
        <div className="mt-16 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 transition-colors duration-200">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Questions Fréquentes
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Comment s'inscrire ?</h4>
              <p className="text-blue-800 dark:text-blue-200 text-sm">
                Consultez notre section "Informations Générales" pour connaître
                les conditions d'admission et la procédure d'inscription.
              </p>
            </div>

            <div className="p-6 bg-green-50 dark:bg-green-900/20 rounded-xl">
              <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">Frais de scolarité ?</h4>
              <p className="text-green-800 dark:text-green-200 text-sm">
                Les informations sur les coûts sont disponibles au secrétariat.
                Contactez-nous pour plus de détails.
              </p>
            </div>

            <div className="p-6 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
              <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">Durée des études ?</h4>
              <p className="text-purple-800 dark:text-purple-200 text-sm">
                La formation dure 3 ans avec environ 40h de cours par semaine,
                alliant théorie et pratique.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
